import { useEffect, useState } from "react";
import OrganizerPillSelect from "../../components/OrganizerPillSelect";
import { EFDatePill, EFTimePill } from "../../components/EventFormPickers";
import { isValidMetaPixelId } from "../../../helpers/metaPixel";
import EventAddressMap from "../EventAddressMap";
import { ciudadesPorProvincia } from "./eventoServicios";
import { esCategoriaDeporte, faltaEntradas, nuevaEntrada, num, sumaCupos } from "./eventoPayload";

const MAX_UPLOAD_BYTES = 10 * 1024 * 1024;
const idDe = (x) => x._id || x.id;
const plata = (n) => `$ ${Number(n || 0).toLocaleString("es-AR")}`;

const Campo = ({ label, children, hint }) => (
  <div className="ef-field">
    <label className="ef-label">{label}</label>
    {children}
    {hint && <p className="ef-footnote">{hint}</p>}
  </div>
);

const Interruptor = ({ titulo, sub, checked, onChange, disabled = false }) => (
  <div className="ef-config-control ef-config-control--switch">
    <span className="ef-switch-text">
      <span className="ef-switch-title">{titulo}</span>
      {sub && <span className="ef-switch-sub">{sub}</span>}
    </span>
    <label className="ef-switch">
      <input type="checkbox" aria-label={titulo} checked={checked} disabled={disabled} onChange={(e) => onChange(e.target.checked)} />
      <span className="ef-track" />
    </label>
  </div>
);

/* ---- 1. Evento ---- */
const PasoEvento = ({ data: d, set, categorias }) => (
  <>
    <div className="pu-grid-3">
      <Campo label="Nombre del evento">
        <input className="ef-input" value={d.nombre} onChange={(e) => set({ nombre: e.target.value })} placeholder="Ej: Noche de Cumbia" />
      </Campo>
      <Campo label="Fecha">
        <EFDatePill value={d.fecha} onChange={(f) => set({ fecha: f ? f.format("YYYY-MM-DD") : "" })} />
      </Campo>
      <div className="pu-grid-2">
        <Campo label="Empieza">
          <EFTimePill value={d.desde} onChange={(val) => set({ desde: val })} />
        </Campo>
        <Campo label="Termina">
          <EFTimePill value={d.hasta} onChange={(val) => set({ hasta: val })} />
        </Campo>
      </div>
    </div>
    <Campo label="Descripción del evento" hint="Es lo que lee la gente antes de sacar su entrada.">
      <textarea className="ef-textarea" style={{ height: 90 }} value={d.descripcion}
        onChange={(e) => set({ descripcion: e.target.value })} placeholder="Describí el evento, horarios, recomendaciones…" />
    </Campo>
    <div style={{ maxWidth: 320 }}>
      <Campo label="Categoría">
        <OrganizerPillSelect
          placeholder="Selecciona categoría"
          value={d.categoriaId}
          onChange={(val) => {
            const cat = categorias.find((c) => String(idDe(c)) === String(val));
            set({ categoriaId: val, categoriaNombre: cat?.name || "" });
          }}
          options={categorias.map((c) => ({ value: idDe(c), label: c.name }))}
        />
      </Campo>
    </div>
  </>
);

/* ---- 2. Ubicación: Provincia -> Ciudad -> Dirección + pin en el mapa, same rules as EventForm ---- */
const PasoDonde = ({ data: d, set, provincias }) => {
  const [ciudades, setCiudades] = useState([]);
  const [fallo, setFallo] = useState("");

  useEffect(() => {
    let vivo = true;
    if (!d.stateid) { setCiudades([]); return undefined; }
    ciudadesPorProvincia(d.stateid)
      .then((l) => vivo && setCiudades(l))
      .catch(() => vivo && setFallo("No pudimos cargar las ciudades."));
    return () => { vivo = false; };
  }, [d.stateid]);

  const etiqueta = (lista, val, campo = "name") => {
    const it = lista.find((x) => String(idDe(x)) === String(val));
    return it ? it[campo] || it.name : "";
  };

  return (
    <>
      {fallo && <div className="ef-alert ef-alert--danger" style={{ margin: 0 }}>{fallo}</div>}
      <div className="pu-grid-2">
        <Campo label="Provincia">
          <OrganizerPillSelect
            placeholder="Selecciona provincia"
            value={d.stateid}
            onChange={(val) => set({
              stateid: val, provinciaLabel: etiqueta(provincias, val),
              cityid: "", ciudadLabel: "",
            })}
            options={provincias.map((s) => ({ value: idDe(s), label: s.name }))}
          />
        </Campo>
        <Campo label="Ciudad">
          <OrganizerPillSelect
            placeholder={ciudades.length ? "Selecciona ciudad" : "Primero provincia"}
            value={d.cityid}
            onChange={(val) => set({ cityid: val, ciudadLabel: etiqueta(ciudades, val) })}
            options={ciudades.map((c) => ({ value: idDe(c), label: c.name }))}
            searchable
            disabled={!ciudades.length}
          />
        </Campo>
      </div>
      <div className="ef-map-stack" style={{ marginBottom: 0 }}>
        <EventAddressMap
          address={d.address}
          lat={d.lat}
          lng={d.lng}
          locality={[d.ciudadLabel, d.provinciaLabel].filter(Boolean).join(", ")}
          onChange={set}
        />
      </div>
    </>
  );
};

/* ---- 3. Entradas ---- */
const PasoEntradas = ({ data: d, set, soloGratis, avisoGratis }) => {
  const setEntrada = (i, patch) => set({ entradas: d.entradas.map((e, j) => (j === i ? { ...e, ...patch } : e)) });
  const dondeTxt = d.address ? `${d.address}, ${d.ciudadLabel}, ${d.provinciaLabel}` : "—";
  return (
    <div className="pu-split">
      <div className="pu-body">
        {soloGratis && <div className="ef-alert ef-alert--info" style={{ margin: 0 }}>{avisoGratis}</div>}
        <div className="pu-modes">
          <button type="button" className="pu-mode" aria-pressed={d.tipo === "gratis"} onClick={() => set({ tipo: "gratis" })}>
            <b>Gratis</b><small>La gente saca su entrada sin pagar.</small>
          </button>
          <button type="button" className="pu-mode" aria-pressed={d.tipo === "pagas"} disabled={soloGratis}
            onClick={() => set({ tipo: "pagas" })}>
            <b>Entradas pagas</b>
            <small>{soloGratis ? "Necesitás tus cobros validados." : "Uno o más tipos de entrada con precio."}</small>
          </button>
        </div>
        <div style={{ maxWidth: 300 }}>
          <Campo label="Cupo total del evento" hint="Cuando se completa, el evento muestra Agotado.">
            <input className="ef-input" type="number" min="0" value={d.capacidad} placeholder="300"
              onChange={(e) => set({ capacidad: e.target.value })} />
          </Campo>
        </div>
        {d.tipo === "pagas" && (
          <>
            <table className="pu-table">
              <thead><tr><th>Tipo de entrada</th><th style={{ width: 150 }}>Precio</th><th style={{ width: 110 }}>Cupo</th><th style={{ width: 40 }} /></tr></thead>
              <tbody>
                {d.entradas.map((e, i) => (
                  <tr key={i}>
                    <td><input className="ef-input" aria-label="Tipo de entrada" value={e.nombre} placeholder="Ej: Anticipada"
                      onChange={(ev) => setEntrada(i, { nombre: ev.target.value })} /></td>
                    <td><div className="pu-money"><span>$</span><input className="ef-input" type="number" min="0" aria-label="Precio"
                      value={e.precio} onChange={(ev) => setEntrada(i, { precio: ev.target.value })} /></div></td>
                    <td><input className="ef-input" type="number" min="0" aria-label="Cupo" value={e.cantidad}
                      onChange={(ev) => setEntrada(i, { cantidad: ev.target.value })} /></td>
                    <td>{d.entradas.length > 1 && (
                      <button type="button" className="ef-btn ef-btn--ghost" aria-label={`Quitar ${e.nombre || "entrada"}`}
                        onClick={() => set({ entradas: d.entradas.filter((_, j) => j !== i) })}>×</button>)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
            <div><button type="button" className="ef-btn ef-btn--ghost" onClick={() => set({ entradas: [...d.entradas, nuevaEntrada()] })}>
              Agregar tipo de entrada</button></div>
          </>
        )}
      </div>
      <aside className="pu-summary" aria-label="Resumen">
        <dl style={{ margin: 0 }}>
          <dt>Evento</dt><dd>{d.nombre || "—"}</dd>
          <dt>Cuándo</dt><dd>{d.fecha || "—"}, {d.desde} a {d.hasta}</dd>
          <dt>Dónde</dt><dd>{dondeTxt}</dd>
          <dt>Cupo</dt><dd>{num(d.capacidad) || 0} personas</dd>
          <dt>Entradas</dt>
          <dd>{d.tipo === "gratis" ? "Gratis" : d.entradas.map((e, i) => (
            <div key={i}>{e.nombre || "—"} {e.precio ? plata(e.precio) : "—"} × {num(e.cantidad) || 0}</div>))}
            {d.tipo === "pagas" && <small>Suman {sumaCupos(d.entradas)} de {num(d.capacidad) || 0}</small>}</dd>
        </dl>
      </aside>
    </div>
  );
};

/* ---- Optional: images ---- */
const Foto = ({ titulo, ayuda, file, onFile }) => {
  const [error, setError] = useState("");
  const [url, setUrl] = useState("");
  useEffect(() => {
    if (!file) { setUrl(""); return undefined; }
    const u = URL.createObjectURL(file);
    setUrl(u);
    return () => URL.revokeObjectURL(u);
  }, [file]);
  const elegir = (e) => {
    const f = e.target.files?.[0] || null;
    if (f && f.size > MAX_UPLOAD_BYTES) {
      setError(`${titulo}: el archivo supera el máximo permitido de 10 MB.`);
      e.target.value = "";
      return;
    }
    setError("");
    onFile(f);
  };
  return (
    <div className="ef-upload-tile">
      <label className={`ef-dropzone ${url ? "has-image" : ""}`}>
        <input type="file" accept="image/*" hidden aria-label={titulo} onChange={elegir} />
        {url
          ? <img src={url} alt={titulo} />
          : <div className="ef-dropzone-placeholder"><strong>Seleccionar archivo</strong><small>Peso máximo 10 MB</small></div>}
      </label>
      <h4>{titulo}</h4>
      <p className="ef-hint">{ayuda}</p>
      {url && <button type="button" className="pu-link" onClick={() => onFile(null)}>Quitar</button>}
      {error && <p className="ef-hint" style={{ color: "var(--ef-danger)" }}>{error}</p>}
    </div>
  );
};

const PasoImagenes = ({ data: d, set }) => {
  const img = (k) => (f) => set({ imagenes: { ...d.imagenes, [k]: f } });
  return (
    <>
      <div className="pu-photo-grid">
        <Foto titulo="Portada de venta" ayuda="Horizontal. 1600 x 900 px." file={d.imagenes.portada} onFile={img("portada")} />
        <Foto titulo="Galería | Escritorio" ayuda="1468 x 920 px." file={d.imagenes.galeria} onFile={img("galeria")} />
        <Foto titulo="Portada móvil" ayuda="Vertical. 1080 x 1920 px." file={d.imagenes.portadaMovil} onFile={img("portadaMovil")} />
        <Foto titulo="Galería | Móvil" ayuda="1080 x 675 px." file={d.imagenes.galeriaMovil} onFile={img("galeriaMovil")} />
      </div>
      <div style={{ maxWidth: 340 }}>
        <Campo label="Instagram del evento">
          <input className="ef-input" value={d.instagram} placeholder="@usuario o URL del perfil"
            onChange={(e) => set({ instagram: e.target.value })} />
        </Campo>
      </div>
    </>
  );
};

/* ---- Optional: purchase conditions ---- */
const PasoCompra = ({ data: d, set }) => {
  const setEntrada = (i, patch) => set({ entradas: d.entradas.map((e, j) => (j === i ? { ...e, ...patch } : e)) });
  return (
    <>
      <div className="pu-grid-2">
        <Interruptor titulo="Reservas habilitadas" sub="Permitir reservaciones previas" checked={d.booking} onChange={(v) => set({ booking: v })} />
        <Campo label="Máximo por persona">
          <input className="ef-input" type="number" min="1" value={d.maxPorPersona} onChange={(e) => set({ maxPorPersona: e.target.value })} />
        </Campo>
      </div>
      {d.tipo === "pagas" && (
        <>
          <table className="pu-table">
            <thead><tr><th>Entrada</th><th style={{ width: 130 }}>Entradas incluidas</th><th>Descripción</th><th style={{ width: 90 }}>Activo</th></tr></thead>
            <tbody>
              {d.entradas.map((e, i) => (
                <tr key={i}>
                  <td><b>{e.nombre}</b></td>
                  <td><input className="ef-input" type="number" min="1" aria-label={`Entradas incluidas de ${e.nombre}`} value={e.incluye}
                    onChange={(ev) => setEntrada(i, { incluye: ev.target.value })} /></td>
                  <td><input className="ef-input" aria-label={`Descripción de ${e.nombre}`} value={e.desc} placeholder="Opcional: qué incluye"
                    onChange={(ev) => setEntrada(i, { desc: ev.target.value })} /></td>
                  <td><label className="ef-switch"><input type="checkbox" aria-label={`Activo ${e.nombre}`} checked={e.activo}
                    onChange={(ev) => setEntrada(i, { activo: ev.target.checked })} /><span className="ef-track" /></label></td>
                </tr>
              ))}
            </tbody>
          </table>
          <p className="ef-hint">Entradas incluidas sirve para combos: una entrada que vale por 2 personas.</p>
        </>
      )}
    </>
  );
};

/* ---- Optional: coupons ---- */
const PasoCupones = ({ data: d, set }) => {
  const setCupon = (i, patch) => set({ cupones: d.cupones.map((c, j) => (j === i ? { ...c, ...patch } : c)) });
  return (
    <>
      {d.cupones.length === 0 ? (
        <p className="pu-empty">Todavía no hay cupones.</p>
      ) : (
        <table className="pu-table">
          <thead><tr><th>Código</th><th style={{ width: 150 }}>Descuento</th><th style={{ width: 170 }}>Tipo</th><th style={{ width: 40 }} /></tr></thead>
          <tbody>
            {d.cupones.map((c, i) => (
              <tr key={i}>
                <td><input className="ef-input" aria-label="Código" value={c.codigo} placeholder="Ej: PREVENTA" onChange={(e) => setCupon(i, { codigo: e.target.value })} /></td>
                <td><input className="ef-input" type="number" min="0" aria-label="Descuento" value={c.descuento} onChange={(e) => setCupon(i, { descuento: e.target.value })} /></td>
                <td><select className="ef-input" aria-label="Tipo de descuento" value={c.tipo} onChange={(e) => setCupon(i, { tipo: e.target.value })}>
                  <option value="fixed">Monto fijo</option><option value="percent">Porcentaje %</option></select></td>
                <td><button type="button" className="ef-btn ef-btn--ghost" aria-label="Quitar cupón"
                  onClick={() => set({ cupones: d.cupones.filter((_, j) => j !== i) })}>×</button></td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
      <div><button type="button" className="ef-btn ef-btn--ghost"
        onClick={() => set({ cupones: [...d.cupones, { codigo: "", descuento: "", tipo: "percent" }] })}>Agregar cupón</button></div>
    </>
  );
};

/* ---- Optional: sponsors + Meta Pixel ---- */
const PasoDifusion = ({ data: d, set }) => {
  const setSponsor = (i, patch) => set({ sponsors: d.sponsors.map((s, j) => (j === i ? { ...s, ...patch } : s)) });
  return (
    <div className="pu-grid-2">
      <div className="pu-body">
        <div className="ef-label">Patrocinadores</div>
        {d.sponsors.length === 0 && <p className="pu-empty">Sin patrocinadores todavía.</p>}
        {d.sponsors.map((s, i) => (
          <div key={i} className="pu-row-actions">
            <input className="ef-input" aria-label="Nombre del patrocinador" value={s.nombre} placeholder="Nombre del patrocinador"
              onChange={(e) => setSponsor(i, { nombre: e.target.value })} />
            <label className="ef-btn ef-btn--ghost" style={{ whiteSpace: "nowrap" }}>
              {s.file ? "Logo cargado" : "Subir logo"}
              <input type="file" accept="image/*" hidden aria-label="Logo del patrocinador"
                onChange={(e) => setSponsor(i, { file: e.target.files?.[0] || null })} />
            </label>
            <button type="button" className="ef-btn ef-btn--ghost" aria-label="Quitar patrocinador"
              onClick={() => set({ sponsors: d.sponsors.filter((_, j) => j !== i) })}>×</button>
          </div>
        ))}
        <div><button type="button" className="ef-btn ef-btn--ghost"
          onClick={() => set({ sponsors: [...d.sponsors, { nombre: "", file: null }] })}>Agregar patrocinador</button></div>
      </div>
      <div className="pu-body">
        <div className="ef-label">Meta Pixel</div>
        <Campo label="Pixel ID" hint="Solo el número, no el código del script.">
          <input className="ef-input" inputMode="numeric" value={d.pixel} placeholder="12345678912345"
            onChange={(e) => set({ pixel: e.target.value.replace(/\D+/g, "").slice(0, 20) })} />
        </Campo>
        <Interruptor titulo="Activar Meta Pixel" checked={d.pixelOn} disabled={!d.pixel.trim()} onChange={(v) => set({ pixelOn: v })} />
      </div>
    </div>
  );
};

/* ---- Optional: certificates ---- */
const PasoCertificados = ({ data: d, set }) => (
  <>
    <Interruptor titulo="Enviar certificados a los asistentes" sub="Se arma con los datos de abajo y la asistencia del evento."
      checked={d.cert} onChange={(v) => set({ cert: v })} />
    {d.cert && (
      <>
        <div className="pu-grid-3">
          <Campo label="Título"><input className="ef-input" value={d.certTitulo} onChange={(e) => set({ certTitulo: e.target.value })} /></Campo>
          <Campo label="Organización"><input className="ef-input" value={d.certOrg} onChange={(e) => set({ certOrg: e.target.value })} /></Campo>
          <div className="pu-grid-2">
            <Campo label="Firmante"><input className="ef-input" value={d.certFirmante} placeholder="Nombre" onChange={(e) => set({ certFirmante: e.target.value })} /></Campo>
            <Campo label="Cargo"><input className="ef-input" value={d.certCargo} placeholder="Ej: Director" onChange={(e) => set({ certCargo: e.target.value })} /></Campo>
          </div>
        </div>
        <Campo label="Texto del certificado" hint="Podés usar {NOMBRE}, {EVENTO} y {FECHA}.">
          <textarea className="ef-textarea" style={{ height: 70 }} value={d.certCuerpo} onChange={(e) => set({ certCuerpo: e.target.value })} />
        </Campo>
        <div className="pu-grid-2">
          <Interruptor titulo="Enviar por mail a asistentes" sub="Se envía a quienes registraron asistencia." checked={d.certMail} onChange={(v) => set({ certMail: v })} />
          {d.certMail && <Campo label="Asunto del mail"><input className="ef-input" value={d.certAsunto} onChange={(e) => set({ certAsunto: e.target.value })} /></Campo>}
        </div>
      </>
    )}
  </>
);

export const pasosEvento = ({ categorias, provincias, soloGratis, avisoGratis }) => ({
  req: [
    {
      id: "evento", titulo: "¿Qué evento vas a hacer?", bajada: "Con esto armamos la página del evento.",
      render: (p) => <PasoEvento {...p} categorias={categorias} />,
      falta: (d) => (!d.nombre.trim() ? "Falta el nombre del evento" : !d.fecha ? "Falta la fecha"
        : !d.desde || !d.hasta ? "Falta el horario" : !d.descripcion.trim() ? "Falta la descripción"
        : !d.categoriaId ? "Elegí la categoría" : ""),
    },
    {
      id: "donde", titulo: "¿Dónde es?",
      bajada: "Elegí provincia, ciudad y la dirección donde se hace, y confirmá el pin en el mapa. Es la misma ubicación que pide el formulario de eventos.",
      render: (p) => <PasoDonde {...p} provincias={provincias} />,
      falta: (d) => (!d.stateid ? "Elegí la provincia" : !d.cityid ? "Elegí la ciudad" : !d.address.trim() ? "Ingresá la dirección" : ""),
    },
    {
      id: "entradas", titulo: "Entradas", final: "Publicar evento",
      bajada: "Elegí si el evento es gratis o con entrada paga, y cuántas personas entran.",
      render: (p) => <PasoEntradas {...p} soloGratis={soloGratis} avisoGratis={avisoGratis} />,
      falta: faltaEntradas,
    },
  ],
  opc: [
    { id: "imagenes", titulo: "Imágenes y redes", seccion: "Configuración visual",
      bajada: "Un evento con portada vende mucho más. Cada imagen puede pesar hasta 10 MB.",
      render: (p) => <PasoImagenes {...p} />, falta: () => "" },
    { id: "compra", titulo: "Condiciones de compra", seccion: "Configuración general",
      bajada: "Cuántas entradas puede sacar cada persona y, si son pagas, el detalle de cada tipo.",
      render: (p) => <PasoCompra {...p} />,
      falta: (d) => (!(num(d.maxPorPersona) >= 1) ? "El máximo por persona tiene que ser 1 o más"
        : d.tipo === "pagas" && d.entradas.some((e) => !(num(e.incluye) >= 1)) ? "Entradas incluidas tiene que ser 1 o más" : "") },
    { id: "cupones", titulo: "Cupones de descuento", seccion: "Cupones",
      bajada: "Códigos que la gente ingresa al comprar. Quedan asociados a este evento.",
      si: (d) => d.tipo === "pagas", render: (p) => <PasoCupones {...p} />,
      falta: (d) => (d.cupones.some((c) => !c.codigo.trim()) ? "Hay un cupón sin código"
        : d.cupones.some((c) => !(num(c.descuento) > 0)) ? "Falta el descuento de un cupón"
        : d.cupones.some((c) => c.tipo === "percent" && num(c.descuento) > 100) ? "Un porcentaje no puede pasar de 100" : "") },
    { id: "difusion", titulo: "Patrocinadores y Meta Pixel", seccion: "Patrocinadores · Marketing",
      bajada: "Los logos de quienes acompañan el evento y tu Pixel de Meta para medir campañas.",
      render: (p) => <PasoDifusion {...p} />,
      falta: (d) => (d.pixel && !isValidMetaPixelId(d.pixel) ? "El Pixel ID debe tener entre 10 y 20 números"
        : d.sponsors.some((s) => !s.nombre.trim()) ? "Hay un patrocinador sin nombre"
        : d.sponsors.some((s) => !s.file) ? "Hay un patrocinador sin logo" : "") },
    { id: "certificados", titulo: "Certificados para asistentes", seccion: "Certificados",
      bajada: "El diploma y el correo que reciben quienes registraron asistencia.",
      si: (d) => esCategoriaDeporte(d.categoriaNombre), render: (p) => <PasoCertificados {...p} />,
      falta: (d) => (d.cert && !d.certTitulo.trim() ? "Falta el título del certificado" : "") },
  ],
});
